import React, { useState, useEffect } from 'react';
import { API_BASE_URL } from '../services/config';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  LinearProgress,
  Tabs,
  Tab,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Visibility,
  Search,
  FilterList,
  GetApp,
  Person,
  Business,
  Email,
  Phone,
  LocationOn,
  Schedule,
  Assignment,
  AttachFile,
  Notifications,
  Security,
  Settings,
  Dashboard,
  Description,
  PhotoCamera,
  Message,
  CheckCircle,
  Warning,
  Info,
} from '@mui/icons-material';
import { clientPortalService } from '../services/clientPortalService';
import { projectsService } from '../services/projectsService';
import { companyService } from '../services/companyService';

interface Client {
  id: string;
  name: string;
  company: string;
  email: string;
  phone: string;
  address: string;
  projects: string[];
  status: 'active' | 'inactive' | 'pending' | 'suspended';
  lastLogin: string;
  joinDate: string;
  totalProjects: number;
  activeProjects: number;
  notifications: number;
  avatar?: string;
}

interface Project {
  id: string;
  name: string;
  client: string;
  status: 'active' | 'planning' | 'in_progress' | 'completed' | 'on_hold' | 'on-hold' | 'cancelled';
  progress: number;
  startDate: string;
  endDate: string;
  budget: number;
  spent: number;
  manager: string;
  description: string;
}

interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'warning' | 'success' | 'error' | 'reminder';
  timestamp: string;
  read: boolean;
  clientId: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`client-portal-tabpanel-${index}`}
      aria-labelledby={`client-portal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}





const ClientPortal: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [newContactName, setNewContactName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newStreet, setNewStreet] = useState('');
  const [newCity, setNewCity] = useState('');
  const [newState, setNewState] = useState('');
  const [newZip, setNewZip] = useState('');
  const [newCountry, setNewCountry] = useState('USA');
  const [addingClient, setAddingClient] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);
  const [companies, setCompanies] = useState<Array<{ id: string; name: string }>>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<string>('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);

  // Send Notification dialog state
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendTitle, setSendTitle] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [sendType, setSendType] = useState<'info' | 'warning' | 'error' | 'success' | 'reminder'>('info');
  const [sendPriority, setSendPriority] = useState<'low' | 'medium' | 'high' | 'urgent'>('medium');
  const [sendingNotification, setSendingNotification] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sendSuccess, setSendSuccess] = useState<boolean>(false);

  // Edit Client dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editCompanyName, setEditCompanyName] = useState('');
  const [editContactName, setEditContactName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editStreet, setEditStreet] = useState('');
  const [editCity, setEditCity] = useState('');
  const [editState, setEditState] = useState('');
  const [editZip, setEditZip] = useState('');
  const [editCountry, setEditCountry] = useState('USA');
  const [savingClientEdits, setSavingClientEdits] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [clientsData, projectsData] = await Promise.all([
          clientPortalService.getClients(),
          clientPortalService.getClientProjects('all') // Assuming we want all projects
        ]);
        
        // Transform API data to match local interfaces
        const transformedClients = clientsData.map(client => ({
          id: client._id,
          name: `${client.contactPerson.firstName} ${client.contactPerson.lastName}`,
          company: client.companyName,
          email: client.contactPerson.email,
          phone: client.contactPerson.phone,
          address: `${client.address.street}, ${client.address.city}, ${client.address.state} ${client.address.zipCode}`,
          projects: client.projects,
          status: client.status,
          lastLogin: client.portalAccess.lastLogin || '',
          joinDate: client.createdAt,
          totalProjects: client.projects.length,
          activeProjects: client.projects.length, // This would need proper calculation
          notifications: 0 // This would need proper calculation
        }));
        
        const transformedProjects = projectsData.map(project => ({
          id: project._id,
          name: project.name,
          client: '', // Would need to be populated from client data
          status: project.status,
          progress: project.progress,
          startDate: project.startDate,
          endDate: project.endDate || '',
          budget: project.budget.total,
          spent: project.budget.spent,
          manager: '', // Would need to be populated
          description: project.description
        }));
        
        setClients(transformedClients);
        setProjects(transformedProjects);
        setNotifications([]); // For now, empty notifications
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const loadCompanies = async () => {
      try {
        setLoadingCompanies(true);
        const list = await companyService.getAllCompanies();
        const normalized = Array.isArray(list)
          ? list.map((c: any) => ({ id: String(c.id || c._id || ''), name: String(c.name || c.legalName || '') }))
          : [];
        setCompanies(normalized);
        // Preselect first company if available and none selected yet
        if (!selectedCompanyId && normalized.length > 0) {
          setSelectedCompanyId(normalized[0].id);
        }
      } catch (e) {
        console.error('Error loading companies:', e);
      } finally {
        setLoadingCompanies(false);
      }
    };
    if (addDialogOpen) {
      loadCompanies();
    }
  }, [addDialogOpen]);

  // Ensure arrays are always arrays
  const clientsArray = Array.isArray(clients) ? clients : [];
  const projectsArray = Array.isArray(projects) ? projects : [];
  const notificationsArray = Array.isArray(notifications) ? notifications : [];

  const filteredClients = clientsArray.filter((client) => {
    const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.company.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         client.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || client.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const totalClients = clientsArray.length;
  const activeClients = clientsArray.filter(c => c.status === 'active').length;
  const pendingClients = clientsArray.filter(c => c.status === 'pending').length;
  const totalNotifications = notificationsArray.filter(n => !n.read).length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active': return 'success';
      case 'pending': return 'warning';
      case 'inactive': return 'default';
      default: return 'default';
    }
  };

  const getProjectStatusColor = (status: string) => {
    switch (status) {
      case 'planning': return 'info';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'on_hold': return 'default';
      default: return 'default';
    }
  };

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success': return <CheckCircle color="success" />;
      case 'warning': return <Warning color="warning" />;
      case 'error': return <Warning color="error" />;
      case 'info': return <Info color="info" />;
      default: return <Info />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const handleViewClient = (client: Client) => {
    setSelectedClient(client);
    setViewDialogOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getClientProjects = (clientCompany: string) => {
    return projectsArray.filter(project => project.client === clientCompany);
  };

  const getClientNotifications = (clientId: string) => {
    return notificationsArray.filter(notification => notification.clientId === clientId);
  };

  const API_URL = API_BASE_URL;

  const resolveRecipientIdByEmail = async (email: string): Promise<string | null> => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/users`, {
        params: { search: email, limit: 1 },
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      const users = response?.data?.users || [];
      if (Array.isArray(users) && users.length > 0 && users[0]?._id) {
        return String(users[0]._id);
      }
      return null;
    } catch (err) {
      console.error('Error resolving recipient by email:', err);
      return null;
    }
  };

  const openSendDialogForSelected = () => {
    if (!selectedClient) return;
    setSendDialogOpen(true);
    setSendTitle(`Message for ${selectedClient.name}`);
    setSendMessage('');
    setSendType('info');
    setSendPriority('medium');
    setSendError(null);
    setSendSuccess(false);
  };

  const handleSendNotification = async () => {
    if (!selectedClient) return;
    try {
      setSendingNotification(true);
      setSendError(null);
      setSendSuccess(false);
      const recipientId = await resolveRecipientIdByEmail(selectedClient.email);
      if (!recipientId) {
        setSendError('Unable to find a user account for this client email.');
        return;
      }
      const token = localStorage.getItem('token');
      const payload = {
        recipient: recipientId,
        title: sendTitle || `Message for ${selectedClient.name}`,
        message: sendMessage,
        type: sendType,
        priority: sendPriority,
      };
      const response = await axios.post(`${API_URL}/notifications`, payload, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      // Append to local notifications list for UX feedback
      const created = response?.data;
      const newNotification: Notification = {
        id: String(created?._id || Date.now()),
        title: String(created?.title || payload.title),
        message: String(created?.message || payload.message),
        type: (created?.type || payload.type) as Notification['type'],
        timestamp: String(created?.createdAt || new Date().toISOString()),
        read: Boolean(created?.read || false),
        clientId: selectedClient.id,
      };
      setNotifications(prev => [newNotification, ...prev]);
      setSendSuccess(true);
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Failed to send notification';
      setSendError(message);
      console.error('Send notification error:', err);
    } finally {
      setSendingNotification(false);
    }
  };

  const openEditDialogForSelected = () => {
    if (!selectedClient) return;
    setEditDialogOpen(true);
    setEditCompanyName(selectedClient.company || '');
    setEditContactName(selectedClient.name || '');
    setEditEmail(selectedClient.email || '');
    setEditPhone(selectedClient.phone || '');
    // Parse address string into parts if possible: "street, city, state zip"
    const addr = selectedClient.address || '';
    const [streetPart, cityStateZip] = addr.split(',').map(s => s.trim());
    setEditStreet(streetPart || '');
    if (cityStateZip) {
      const parts = cityStateZip.split(' ').filter(Boolean);
      // Assume format: City, State ZIP or City State ZIP
      const cityFromComma = cityStateZip.includes(',') ? cityStateZip.split(',')[0].trim() : parts[0] || '';
      const statePart = cityStateZip.includes(',') ? cityStateZip.split(',')[1]?.trim().split(' ')[0] || '' : parts[1] || '';
      const zipPart = cityStateZip.includes(',') ? cityStateZip.split(',')[1]?.trim().split(' ')[1] || '' : parts[2] || '';
      setEditCity(cityFromComma);
      setEditState(statePart);
      setEditZip(zipPart);
    } else {
      setEditCity('');
      setEditState('');
      setEditZip('');
    }
    setEditCountry('USA');
    setEditError(null);
  };

  const handleSaveClientEdits = async () => {
    if (!selectedClient) return;
    const projectIds = Array.isArray(selectedClient.projects) ? selectedClient.projects : [];
    const targetProjectId = projectIds[0];
    if (!targetProjectId) {
      setEditError('No associated project found to update client details.');
      return;
    }
    try {
      setSavingClientEdits(true);
      setEditError(null);
      const updatePayload: any = {
        client: {
          name: editCompanyName,
          email: editEmail,
          phone: editPhone,
          address: {
            street: editStreet,
            city: editCity,
            state: editState,
            zipCode: editZip,
            country: editCountry || 'USA',
          },
          contactPerson: {
            name: editContactName,
            email: editEmail,
            phone: editPhone,
            title: '',
          },
        },
      };
      const resp = await projectsService.update(targetProjectId, updatePayload);
      if (resp?.data) {
        // Refresh clients list to reflect edits
        const clientsData = await clientPortalService.getClients();
        const transformedClients = clientsData.map(client => ({
          id: client._id,
          name: `${client.contactPerson.firstName} ${client.contactPerson.lastName}`.trim(),
          company: client.companyName,
          email: client.contactPerson.email,
          phone: client.contactPerson.phone,
          address: `${client.address.street}, ${client.address.city}, ${client.address.state} ${client.address.zipCode}`.trim(),
          projects: client.projects,
          status: client.status,
          lastLogin: client.portalAccess.lastLogin || '',
          joinDate: client.createdAt,
          totalProjects: client.projects.length,
          activeProjects: client.projects.length,
          notifications: 0,
        }));
        setClients(transformedClients);
        setEditDialogOpen(false);
      } else {
        setEditError('Failed to update client details.');
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || err?.message || 'Error updating client';
      setEditError(message);
      console.error('Edit client error:', err);
    } finally {
      setSavingClientEdits(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Client Portal
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          sx={{ bgcolor: 'primary.main' }}
          onClick={() => setAddDialogOpen(true)}
        >
          Add Client
        </Button>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label="Clients" icon={<Person />} />
          <Tab label="Projects" icon={<Assignment />} />
          <Tab label="Notifications" icon={<Badge badgeContent={totalNotifications} color="error"><Notifications /></Badge>} />
          <Tab label="Settings" icon={<Settings />} />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        {/* Summary Cards */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Person color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Total Clients
                  </Typography>
                  <Typography variant="h5">
                    {totalClients}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CheckCircle color="success" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Active Clients
                  </Typography>
                  <Typography variant="h5">
                    {activeClients}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Schedule color="warning" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Pending Clients
                  </Typography>
                  <Typography variant="h5">
                    {pendingClients}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 200, flex: 1 }}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Notifications color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography color="textSecondary" gutterBottom>
                    Unread Notifications
                  </Typography>
                  <Typography variant="h5">
                    {totalNotifications}
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Filters and Search */}
        <Paper sx={{ p: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField
              placeholder="Search clients..."
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ color: 'text.secondary', mr: 1 }} />,
              }}
              sx={{ minWidth: 250 }}
            />
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="outlined"
              startIcon={<FilterList />}
              sx={{ ml: 'auto' }}
            >
              More Filters
            </Button>
            <Button
              variant="outlined"
              startIcon={<GetApp />}
            >
              Export
            </Button>
          </Box>
        </Paper>

        {/* Clients Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Client</TableCell>
                  <TableCell>Company</TableCell>
                  <TableCell>Contact</TableCell>
                  <TableCell>Projects</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Last Login</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ width: 40, height: 40 }}>
                          {client.name.split(' ').map(n => n[0]).join('')}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {client.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            Member since {formatDate(client.joinDate)}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {client.company}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Email sx={{ fontSize: 16 }} />
                          {client.email}
                        </Typography>
                        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Phone sx={{ fontSize: 16 }} />
                          {client.phone}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {client.activeProjects} active / {client.totalProjects} total
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={client.status}
                          color={getStatusColor(client.status) as any}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                        {client.notifications > 0 && (
                          <Badge badgeContent={client.notifications} color="error">
                            <Notifications sx={{ fontSize: 16 }} />
                          </Badge>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDateTime(client.lastLogin)}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewClient(client)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small">
                          <Edit />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Send Message">
                        <IconButton size="small" color="primary">
                          <Message />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Paper>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Project</TableCell>
                  <TableCell>Client</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell>Timeline</TableCell>
                  <TableCell>Manager</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.map((project) => (
                  <TableRow key={project.id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {project.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {project.description}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {project.client}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={project.status.replace('_', ' ')}
                        color={getProjectStatusColor(project.status) as any}
                        size="small"
                        sx={{ textTransform: 'capitalize' }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ width: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={project.progress}
                          sx={{ mb: 0.5 }}
                        />
                        <Typography variant="caption">
                          {project.progress}%
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatCurrency(project.spent)} / {formatCurrency(project.budget)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round((project.spent / project.budget) * 100)}% spent
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {formatDate(project.startDate)} - {formatDate(project.endDate)}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {project.manager}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Recent Notifications
          </Typography>
          <List>
            {notifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    bgcolor: notification.read ? 'transparent' : 'action.hover',
                    borderRadius: 1,
                    mb: 1,
                  }}
                >
                  <ListItemIcon>
                    {getNotificationIcon(notification.type)}
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="body2" fontWeight={notification.read ? 'normal' : 'medium'}>
                          {notification.title}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDateTime(notification.timestamp)}
                        </Typography>
                      </Box>
                    }
                    secondary={notification.message}
                  />
                </ListItem>
                {index < notifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </Paper>
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Portal Settings
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Security />
                  </ListItemIcon>
                  <ListItemText primary="Access Control" secondary="Manage client permissions" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Notifications />
                  </ListItemIcon>
                  <ListItemText primary="Notification Settings" secondary="Configure alerts and updates" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Dashboard />
                  </ListItemIcon>
                  <ListItemText primary="Dashboard Layout" secondary="Customize client dashboard" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
          <Card sx={{ minWidth: 300, flex: 1 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Communication
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <Email />
                  </ListItemIcon>
                  <ListItemText primary="Email Templates" secondary="Manage automated emails" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <Message />
                  </ListItemIcon>
                  <ListItemText primary="Messaging System" secondary="Configure client messaging" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <AttachFile />
                  </ListItemIcon>
                  <ListItemText primary="File Sharing" secondary="Document sharing settings" />
                </ListItem>
              </List>
            </CardContent>
          </Card>
        </Box>
      </TabPanel>

      {/* View Client Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          Client Details - {selectedClient?.name}
        </DialogTitle>
        <DialogContent>
          {selectedClient && (
            <Box sx={{ pt: 2 }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 3 }}>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Company
                  </Typography>
                  <Typography variant="body1">
                    {selectedClient.company}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Email
                  </Typography>
                  <Typography variant="body1">
                    {selectedClient.email}
                  </Typography>
                </Box>
                <Box sx={{ flex: 1, minWidth: 200 }}>
                  <Typography variant="subtitle2" color="text.secondary">
                    Phone
                  </Typography>
                  <Typography variant="body1">
                    {selectedClient.phone}
                  </Typography>
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Address
                </Typography>
                <Typography variant="body1">
                  {selectedClient.address}
                </Typography>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Projects
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {getClientProjects(selectedClient.company).map((project) => (
                    <Chip
                      key={project.id}
                      label={project.name}
                      color={getProjectStatusColor(project.status) as any}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </Box>
              
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Recent Notifications
                </Typography>
                <List sx={{ maxHeight: 200, overflow: 'auto' }}>
                  {getClientNotifications(selectedClient.id).slice(0, 3).map((notification) => (
                    <ListItem key={notification.id} sx={{ py: 1 }}>
                      <ListItemIcon sx={{ minWidth: 32 }}>
                        {getNotificationIcon(notification.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={notification.title}
                        secondary={notification.message}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          <Button startIcon={<Message />} onClick={openSendDialogForSelected}>
            Send Message
          </Button>
          <Button variant="contained" startIcon={<Edit />} onClick={openEditDialogForSelected}>
            Edit Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Send Notification Dialog */}
      <Dialog open={sendDialogOpen} onClose={() => setSendDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Send Notification</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Title" value={sendTitle} onChange={(e) => setSendTitle(e.target.value)} required />
            <TextField label="Message" value={sendMessage} onChange={(e) => setSendMessage(e.target.value)} multiline minRows={3} required />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth>
                <InputLabel id="send-type-label">Type</InputLabel>
                <Select labelId="send-type-label" label="Type" value={sendType} onChange={(e) => setSendType(e.target.value as any)}>
                  <MenuItem value="info">Info</MenuItem>
                  <MenuItem value="success">Success</MenuItem>
                  <MenuItem value="warning">Warning</MenuItem>
                  <MenuItem value="error">Error</MenuItem>
                  <MenuItem value="reminder">Reminder</MenuItem>
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel id="send-priority-label">Priority</InputLabel>
                <Select labelId="send-priority-label" label="Priority" value={sendPriority} onChange={(e) => setSendPriority(e.target.value as any)}>
                  <MenuItem value="low">Low</MenuItem>
                  <MenuItem value="medium">Medium</MenuItem>
                  <MenuItem value="high">High</MenuItem>
                  <MenuItem value="urgent">Urgent</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {sendError && <Typography variant="body2" color="error">{sendError}</Typography>}
            {sendSuccess && <Typography variant="body2" color="success.main">Notification sent successfully.</Typography>}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSendDialogOpen(false)} disabled={sendingNotification}>Cancel</Button>
          <Button variant="contained" onClick={handleSendNotification} disabled={sendingNotification || !sendTitle || !sendMessage}>
            {sendingNotification ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Client Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Edit Client</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField label="Company" value={editCompanyName} onChange={(e) => setEditCompanyName(e.target.value)} required />
            <TextField label="Contact Name" value={editContactName} onChange={(e) => setEditContactName(e.target.value)} required />
            <TextField label="Email" type="email" value={editEmail} onChange={(e) => setEditEmail(e.target.value)} />
            <TextField label="Phone" value={editPhone} onChange={(e) => setEditPhone(e.target.value)} />
            <TextField label="Street" value={editStreet} onChange={(e) => setEditStreet(e.target.value)} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="City" value={editCity} onChange={(e) => setEditCity(e.target.value)} />
              <TextField label="State" value={editState} onChange={(e) => setEditState(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Zip Code" value={editZip} onChange={(e) => setEditZip(e.target.value)} />
              <TextField label="Country" value={editCountry} onChange={(e) => setEditCountry(e.target.value)} />
            </Box>
            {editError && (
              <Typography variant="body2" color="error">{editError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)} disabled={savingClientEdits}>Cancel</Button>
          <Button
            variant="contained"
            disabled={savingClientEdits || !editCompanyName || !editContactName}
            onClick={handleSaveClientEdits}
          >
            {savingClientEdits ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>
      {/* Add Client Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Add Client</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="add-client-company-label">Company</InputLabel>
              <Select
                labelId="add-client-company-label"
                label="Company"
                value={selectedCompanyId}
                onChange={(e) => setSelectedCompanyId(String(e.target.value))}
              >
                {companies.map((c) => (
                  <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField label="Contact Name" value={newContactName} onChange={(e) => setNewContactName(e.target.value)} required />
            <TextField label="Email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
            <TextField label="Phone" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
            <TextField label="Street" value={newStreet} onChange={(e) => setNewStreet(e.target.value)} />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="City" value={newCity} onChange={(e) => setNewCity(e.target.value)} />
              <TextField label="State" value={newState} onChange={(e) => setNewState(e.target.value)} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField label="Zip Code" value={newZip} onChange={(e) => setNewZip(e.target.value)} />
              <TextField label="Country" value={newCountry} onChange={(e) => setNewCountry(e.target.value)} />
            </Box>
            {addError && (
              <Typography variant="body2" color="error">{addError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)} disabled={addingClient}>Cancel</Button>
          <Button
            variant="contained"
            disabled={
              addingClient ||
              !selectedCompanyId ||
              !newContactName ||
              !newStreet ||
              !newCity ||
              !newState ||
              !newZip
            }
            onClick={async () => {
              try {
                setAddError(null);
                setAddingClient(true);
                const projectNumber = `CLIENT-${Date.now()}`;
                const selectedCompany = companies.find(c => c.id === selectedCompanyId);
                const companyName = selectedCompany?.name || 'Unnamed Company';
                // Minimal valid project payload matching backend schema
                const now = new Date();
                const tomorrow = new Date(Date.now() + 24 * 60 * 60 * 1000);
                const payload: any = {
                  name: `Client - ${companyName}`,
                  description: `Client record for ${companyName} (${newContactName})`,
                  projectNumber,
                  status: 'planning',
                  type: 'commercial',
                  company: selectedCompanyId,
                  location: {
                    address: {
                      street: newStreet,
                      city: newCity,
                      state: newState,
                      zipCode: newZip,
                      country: newCountry || 'USA',
                    },
                  },
                  timeline: {
                    startDate: now.toISOString(),
                    endDate: tomorrow.toISOString(),
                  },
                  budget: {
                    totalBudget: 0,
                    currency: 'USD',
                  },
                  client: {
                    name: companyName,
                    email: newEmail,
                    phone: newPhone,
                    address: {
                      street: newStreet,
                      city: newCity,
                      state: newState,
                      zipCode: newZip,
                      country: newCountry || 'USA',
                    },
                    contactPerson: {
                      name: newContactName,
                      email: newEmail,
                      phone: newPhone,
                      title: '',
                    },
                  },
                };

                const resp = await projectsService.create(payload);
                if (resp?.data?.success !== false) {
                  const clientsData = await clientPortalService.getClients();
                  const transformedClients = clientsData.map(client => ({
                    id: client._id,
                    name: `${client.contactPerson.firstName} ${client.contactPerson.lastName}`.trim(),
                    company: client.companyName,
                    email: client.contactPerson.email,
                    phone: client.contactPerson.phone,
                    address: `${client.address.street}, ${client.address.city}, ${client.address.state} ${client.address.zipCode}`.trim(),
                    projects: client.projects,
                    status: client.status,
                    lastLogin: client.portalAccess.lastLogin || '',
                    joinDate: client.createdAt,
                    totalProjects: client.projects.length,
                    activeProjects: client.projects.length,
                    notifications: 0,
                  }));
                  setClients(transformedClients);
                  setAddDialogOpen(false);
                  setNewContactName('');
                  setNewEmail('');
                  setNewPhone('');
                  setNewStreet('');
                  setNewCity('');
                  setNewState('');
                  setNewZip('');
                  setNewCountry('USA');
                  setSelectedCompanyId('');
                } else {
                  setAddError('Failed to create client. Please try again.');
                }
              } catch (err: any) {
                const message = err?.response?.data?.message || err?.message || 'Error adding client';
                setAddError(message);
                console.error('Add client error:', err);
              } finally {
                setAddingClient(false);
              }
            }}
          >
            {addingClient ? 'Adding...' : 'Add Client'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClientPortal;